import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StatCard } from "@/components/app/stat-card";
import { Users, ArrowDownToLine, ArrowUpFromLine, DollarSign } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Admin — CryptoBull Pro" }] }),
  component: AdminOverview,
});

function AdminOverview() {
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [users, dep, wd] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("deposits").select("amount, status, created_at"),
        supabase.from("withdrawals").select("amount, status, created_at"),
      ]);
      const totalDep = (dep.data ?? []).filter((d) => d.status === "approved").reduce((s, d) => s + Number(d.amount), 0);
      const totalWd = (wd.data ?? []).filter((d) => d.status === "approved").reduce((s, d) => s + Number(d.amount), 0);
      const pendingDep = (dep.data ?? []).filter((d) => d.status === "pending").length;
      const pendingWd = (wd.data ?? []).filter((d) => d.status === "pending").length;
      return { users: users.count ?? 0, totalDep, totalWd, pendingDep, pendingWd, depRows: dep.data ?? [] };
    },
  });

  const chartData = (() => {
    const buckets: Record<string, number> = {};
    (stats?.depRows ?? []).forEach((d) => {
      const k = new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      buckets[k] = (buckets[k] ?? 0) + Number(d.amount);
    });
    return Object.entries(buckets).slice(-14).map(([d, v]) => ({ d, v }));
  })();

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={String(stats?.users ?? 0)} icon={Users} accent="primary" />
        <StatCard label="Total Deposits" value={fmtMoney(stats?.totalDep ?? 0)} icon={ArrowDownToLine} accent="success" />
        <StatCard label="Total Withdrawals" value={fmtMoney(stats?.totalWd ?? 0)} icon={ArrowUpFromLine} accent="danger" />
        <StatCard label="Pending Actions" value={String((stats?.pendingDep ?? 0) + (stats?.pendingWd ?? 0))} icon={DollarSign} accent="gold" hint={`${stats?.pendingDep ?? 0} dep · ${stats?.pendingWd ?? 0} wd`} />
      </div>
      <div className="glass rounded-2xl p-6">
        <div className="text-sm font-medium mb-4">Approved deposits — last 14 buckets</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
              <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [fmtMoney(v), "Deposits"]} />
              <Bar dataKey="v" fill="hsl(35 95% 55%)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}